// frontend/src/utils/percent.ts
/**
 * Centralized percent helpers to avoid drift across modules.
 */
export function fromDecimalToPercent(value: number | null | undefined): number {
  if (value === null || value === undefined || isNaN(value)) return NaN;
  return value * 100;
}

export function fromPercentToDecimal(value: number | null | undefined): number {
  if (value === null || value === undefined || isNaN(value)) return NaN;
  return value / 100;
}

export function percentToDecimalString(input: string | number | null | undefined): string {
  if (input === null || input === undefined) return "0";

  // Accept both "4.2" and "4.2%" gracefully (optional: if you don't want this, remove the trailing % strip)
  let s = String(input).trim().replace(",", ".").replace(/%$/, "");
  if (s === "" || !/^[-+]?\d*(\.\d*)?$/.test(s)) return "0";

  let neg = false;
  if (s.startsWith("+") || s.startsWith("-")) {
    neg = s.startsWith("-");
    s = s.slice(1);
  }

  const [intPartRaw, fracPartRaw = ""] = s.split(".");
  const intPart = (intPartRaw || "").replace(/\D/g, "");
  const fracPart = (fracPartRaw || "").replace(/\D/g, "");

  // Keep all digits; do NOT strip leading zeros before shifting
  let digits = (intPart === "" ? "0" : intPart) + fracPart;

  // New fractional length after dividing by 100 = original frac digits + 2
  const newFracLen = fracPart.length + 2;

  // Ensure we have at least (newFracLen + 1) digits so we can insert the decimal point
  const minLen = newFracLen + 1;
  if (digits.length < minLen) digits = digits.padStart(minLen, "0");

  const pos = digits.length - newFracLen;
  let out = digits.slice(0, pos) + "." + digits.slice(pos);

  // Normalize "000.123" → "0.123"
  out = out.replace(/^0+(?=\d)/, "");
  if (out.startsWith(".")) out = "0" + out;

  // Trim trailing zeros in fraction, but keep a single ".0" if it's an integer boundary
  out = out.replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, ".0");

  if (neg && out !== "0" && out !== "0.0") out = "-" + out;
  return out;
}

export function decimalToPercentString(input: string | number | null | undefined): string {
  if (input === null || input === undefined) return "";
  let s = String(input).trim().replace(",", ".");
  if (s === "" || !/^[-+]?\d*(\.\d*)?$/.test(s)) return "";

  let neg = false;
  if (s.startsWith("+") || s.startsWith("-")) {
    neg = s.startsWith("-");
    s = s.slice(1);
  }

  const [intPartRaw, fracPartRaw = ""] = s.split(".");
  const intPart = intPartRaw.replace(/\D/g, "") || "0";
  let fracPart = fracPartRaw.replace(/\D/g, "");

  // Shift decimal point right by 2 (multiply by 100) purely as strings
  // Ensure we have at least 2 fractional digits to pull into the integer
  if (fracPart.length < 2) fracPart = fracPart.padEnd(2, "0");

  // Move the first 2 fractional digits into the integer part
  let intOut = (intPart + fracPart.slice(0, 2)).replace(/^0+/, "") || "0";
  let fracOut = fracPart.slice(2);

  // Trim trailing zeros in the fractional part (e.g., "4200" -> "42")
  fracOut = fracOut.replace(/0+$/, "");

  // Build output
  let out = fracOut ? `${intOut}.${fracOut}` : intOut;

  if (neg && out !== "0") out = "-" + out;
  return out;
}